/**
 * Plan types available in the application
 */
export type PlanType = 'One' | 'Pro' | 'Max'

/**
 * Plan constants
 */
export const PLANS = {
  ONE: 'One',
  PRO: 'Pro',
  MAX: 'Max',
} as const

/**
 * Plan display names (marketing names)
 */
export const PLAN_DISPLAY_NAMES: Record<string, string> = {
  One: 'Starter',
  Pro: 'Business',
  Max: 'Enterprise',
  Free: 'Free',
  Trial: 'Trial',
}

/**
 * Feature identifiers
 */
export const FEATURES = {
  SANDBOX: 'sandbox',
  WEBHOOKS: 'webhooks',
  MANAGED_ACCOUNTS: 'managed_accounts',
  API_TOKENS: 'api_tokens',
  MCP: 'mcp',
  WHITE_LABEL: 'white_label',
  SSO: 'sso',
  MESSAGING_CHANNELS: 'messaging_channels',
  BLOCKCHAIN: 'blockchain',
  ACCESS_POLICY: 'access_policy',
  ACCESS_LINKS: 'access_links',
  ADVANCED_FIELDS: 'advanced_fields',
  PAID_SIGNER_MFA_SMS: 'paid_signer_mfa_sms',
  PAID_SIGNER_MFA_WHATSAPP: 'paid_signer_mfa_whatsapp',
  PAID_SIGNER_MFA_SELFIE: 'paid_signer_mfa_selfie',
  PAID_SIGNER_VERIFIED_SELFIE: 'paid_signer_verified_selfie',
  PAID_SIGNER_FACE_MATCH: 'paid_signer_face_match',
  PAID_SIGNER_IDENTITY_DOCUMENT: 'paid_signer_identity_document',
  PAID_SIGNER_MFA_PIX: 'paid_signer_mfa_pix',
  PAID_SIGNER_MFA_DIGITAL_CERTIFICATE: 'paid_signer_mfa_digital_certificate',
  PAID_COMMUNICATION_SMS: 'paid_communication_sms',
  PAID_COMMUNICATION_WHATSAPP: 'paid_communication_whatsapp',
  TEMPLATES: 'templates',
  DOCUMENT_UPLOAD_SIZE: 'document_upload_size',
  DOCUMENT_COUNT: 'document_count',
  SIGNER_COUNT: 'signer_count',
  USER_MANAGEMENT: 'user_management',
  USER_SEATS: 'user_seats',
  SUSTAINABILITY_BADGE: 'sustainability_badge',
  REDIRECT_URL: 'redirect_url',
  AI_CHAT: 'ai_chat',
  AI_ASSISTANT: 'ai_assistant',
  SCHEDULED_PUBLISH: 'scheduled_publish',
  REMINDERS: 'reminders',
  EXPIRATION: 'expiration',
  MARK_ORIENTATION: 'mark_orientation',
  ENVELOPE_BALANCE: 'envelope_balance',
  LTV: 'ltv',
  SEAL: 'seal',
  ENFORCE_2FA: 'enforce_2fa',
  BULK_SIGN: 'bulk_sign',
} as const

/**
 * Plan hierarchy for access control
 * Free < One < Pro < Max
 */
export const PLAN_HIERARCHY: Record<string, number> = {
  Free: 0,
  One: 1,
  Pro: 2,
  Max: 3,
}

/**
 * Plan limits configuration
 */
export interface PlanLimits {
  maxDocumentSizeMB: number
  maxDocumentsPerEnvelope: number
  maxSignersPerEnvelope: number
}

export const PLAN_LIMITS: Record<string, PlanLimits> = {
  Free: {
    maxDocumentSizeMB: 5,
    maxDocumentsPerEnvelope: 2,
    maxSignersPerEnvelope: 3,
  },
  One: {
    maxDocumentSizeMB: 25,
    maxDocumentsPerEnvelope: 20,
    maxSignersPerEnvelope: 20,
  },
  Pro: {
    maxDocumentSizeMB: 50,
    maxDocumentsPerEnvelope: 50,
    maxSignersPerEnvelope: 50,
  },
  Max: {
    maxDocumentSizeMB: 100,
    maxDocumentsPerEnvelope: Infinity,
    maxSignersPerEnvelope: Infinity,
  },
}

/**
 * Get plan limits for a given feature type
 */
export function getPlanLimits(
  featureType: string | null | undefined,
): PlanLimits {
  return PLAN_LIMITS[featureType ?? 'Free'] ?? PLAN_LIMITS.Free
}

/**
 * Get the minimum plan required to support a given document size in bytes
 * Returns null if no plan supports it (> 100MB)
 */
export function getMinimumPlanForDocumentSize(
  sizeBytes: number,
): PlanType | null {
  const sizeMB = sizeBytes / (1024 * 1024)
  const planOrder: Array<PlanType> = ['One', 'Pro', 'Max']
  for (const plan of planOrder) {
    if (PLAN_LIMITS[plan].maxDocumentSizeMB >= sizeMB) {
      return plan
    }
  }
  return null
}

/**
 * Get the minimum plan that would allow a given document count
 * Returns null if current plan already supports it
 */
export function getNextPlanForDocumentCount(
  currentFeatureType: string | null | undefined,
  requiredCount: number,
): PlanType | null {
  const currentLevel = PLAN_HIERARCHY[currentFeatureType ?? 'Free'] ?? 0
  const planOrder: Array<PlanType> = ['One', 'Pro', 'Max']
  for (const plan of planOrder) {
    if (
      PLAN_HIERARCHY[plan] > currentLevel &&
      PLAN_LIMITS[plan].maxDocumentsPerEnvelope >= requiredCount
    ) {
      return plan
    }
  }
  return null
}

/**
 * Get the minimum plan that would allow a given signer count
 * Returns null if current plan already supports it
 */
export function getNextPlanForSignerCount(
  currentFeatureType: string | null | undefined,
  requiredCount: number,
): PlanType | null {
  const currentLevel = PLAN_HIERARCHY[currentFeatureType ?? 'Free'] ?? 0
  const planOrder: Array<PlanType> = ['One', 'Pro', 'Max']
  for (const plan of planOrder) {
    if (
      PLAN_HIERARCHY[plan] > currentLevel &&
      PLAN_LIMITS[plan].maxSignersPerEnvelope >= requiredCount
    ) {
      return plan
    }
  }
  return null
}

/**
 * Next plan to pitch when the user hits the seat limit. Free (and the legacy
 * Trial enum the BE may still emit) upgrade to One; One to Pro; Pro and
 * above pitch Max.
 */
export function getNextPlanForUserSeats(
  featureType: string | null | undefined,
): PlanType {
  switch (featureType) {
    case 'Free':
    case 'Trial':
      return PLANS.ONE
    case 'One':
      return PLANS.PRO
    case 'Pro':
    default:
      return PLANS.MAX
  }
}
